import { useEffect, useRef } from 'react';
import { createBrowserRouter, createMemoryRouter, RouterProvider, Navigate, Outlet, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { i18n } from './locale/i18n.js';
import { normalizeLocale, localeFromSegment, localeToUrl, SUPPORTED_URL_LOCALES } from '../../shared-site-web/locales.js';
import { AppCtx } from '../../web/src/app-context.jsx';
import { HeadProvider } from './layout/head-context.js';
import { ScrollToTop } from './layout/scroll-to-top.jsx';
import { SiteHeader } from './layout/site-header.jsx';
import { SiteFooter } from './layout/site-footer.jsx';
import { HomePage } from './pages/home.jsx';
import { PricingPage } from './pages/pricing.jsx';
import { StoryPage } from './pages/story.jsx';
import { TermsPage } from './pages/terms.jsx';
import { PrivacyPage } from './pages/privacy.jsx';
import { NotFoundPage } from './pages/not-found.jsx';
import { UseCasesIndexPage } from './pages/use-cases-index.jsx';
import { UseCasePage } from './use-cases/use-case-page.jsx';

/**
 * @returns {string}
 */
function detectDefaultLang() {
	if (typeof navigator !== 'undefined' && navigator.language) {
		return `/${localeToUrl(normalizeLocale(navigator.language))}/`;
	}
	return '/en/';
}

/**
 * Synchronously matches i18next to the URL language so the first paint is
 * already in the right language. main.jsx calls this before React renders;
 * the layout effect below handles toggle navigation afterwards. The URL
 * segment is lowercase (/zh-cn) — map it to the canonical locale code,
 * preserving /en-us → en-US.
 * @param {string} lang
 * @returns {void}
 */
function syncLanguage(lang) {
	const locale = localeFromSegment(lang);
	if (i18n.language !== locale) {
		i18n.changeLanguage(locale);
	}
}

/**
 * @returns {JSX.Element}
 */
function LangLayout() {
	const { lang } = useParams();
	const { i18n: i18nInstance } = useTranslation();

	useEffect(() => {
		syncLanguage(/** @type {string} */ (lang));
		document.documentElement.lang = localeFromSegment(/** @type {string} */ (lang));
	}, [lang, i18nInstance]);

	if (!SUPPORTED_URL_LOCALES.includes(/** @type {string} */ (lang))) {
		return <Navigate to={detectDefaultLang()} replace />;
	}

	return (
		<div className="site">
			<ScrollToTop />
			<SiteHeader />
			<main className="site-main">
				<Outlet />
			</main>
			<SiteFooter />
		</div>
	);
}

/**
 * @returns {import('react-router-dom').RouteObject[]}
 */
export function createRoutes() {
	return [
		{ path: '/', element: <Navigate to={detectDefaultLang()} replace /> },
		{
			path: '/:lang',
			element: <LangLayout />,
			children: [
				{ index: true, element: <HomePage /> },
				{ path: 'use-cases', element: <UseCasesIndexPage /> },
				{ path: 'use-cases/:slug', element: <UseCasePage /> },
				{ path: 'pricing', element: <PricingPage /> },
				{ path: 'story', element: <StoryPage /> },
				{ path: 'terms', element: <TermsPage /> },
				{ path: 'privacy', element: <PrivacyPage /> },
				{ path: '*', element: <NotFoundPage /> },
			],
		},
	];
}

/**
 * Browser router used by the client entry (main.jsx).
 * @returns {import('react-router-dom').Router}
 */
export function createBrowserApp() {
	return createBrowserRouter(createRoutes());
}

/**
 * Memory router used by the SSR prerender to render one concrete path.
 * @param {string} initialPath
 * @returns {import('react-router-dom').Router}
 */
export function createMemoryApp(initialPath) {
	return createMemoryRouter(createRoutes(), { initialEntries: [initialPath] });
}

/**
 * No-op app context so reused web components (DataTable/TableBody) resolve
 * `dateFormat` without wiring a live backend.
 * @type {import('../../web/src/app-context.jsx').AppCtxValue}
 */
const SITE_APP_CTX = {
	showToast: () => {},
	toast: null,
	pendingCount: 0,
	setPendingCount: () => {},
	dateFormat: 'iso',
	setDateFormat: () => {},
	dateFormatSetting: 'auto',
	setDateFormatSetting: () => {},
	onboardingStatus: null,
	setOnboardingStatus: () => {},
	privateMode: false,
	setPrivateMode: () => {},
};

/**
 * @param {{ router: import('react-router-dom').Router, headRef: { current: { title: string | null, description: string | null } } }} props
 * @returns {JSX.Element}
 */
export function AppRoot({ router, headRef }) {
	return (
		<HeadProvider headRef={headRef}>
			<AppCtx.Provider value={SITE_APP_CTX}>
				<RouterProvider router={router} />
			</AppCtx.Provider>
		</HeadProvider>
	);
}

/**
 * @returns {JSX.Element}
 */
export function App() {
	const routerRef = useRef(null);
	if (routerRef.current === null) { routerRef.current = createBrowserApp(); }
	const headRef = useRef({ title: null, description: null });
	return <AppRoot router={routerRef.current} headRef={headRef} />;
}
